/**
 * Send back (#190): what the Review Queue tells a coordinator before they
 * press it, and the facts that copy depends on.
 *
 * `send_back_assignment` marks the reviewed attempt SentBack and resets the
 * assignment to NotStarted. The person's next start opens a new attempt from
 * the first stop; the earlier attempts' answers stay on record (keyed by
 * attempt number), though this queue scores only the latest attempt's checks
 * (the step-timing table lists every attempt). A SOLO assignment keeps its own
 * session. A GROUP member is moved out of the group's shared room into a fresh
 * session of their own (the module's `send_back_session`), so they redo it
 * self-paced unless a coordinator regroups them — except when the project has
 * no agent to create that session with, in which case they stay in the room.
 * The dialog has to say which of these is about to happen.
 */
import type { GroupMemberState } from '@/lib/groupRunEntity';

/**
 * Assignment ids that are members of a group run, whatever the run's status.
 * Membership is derived server-side from the session an assignment points at,
 * so a finished run still lists everyone who ran in it, which is exactly the
 * set the module moves to a solo session on send-back.
 */
export function groupMemberAssignmentIds(
  runs: readonly { members: readonly Pick<GroupMemberState, 'assignmentId'>[] }[],
): Set<string> {
  const ids = new Set<string>();
  for (const run of runs) {
    for (const m of run.members) ids.add(String(m.assignmentId));
  }
  return ids;
}

/** Body of the Send back confirmation. */
export function sendBackConfirmBody(facts: {
  isGroupMember: boolean;
  /** Whether the site has an onboarding agent to create a fresh session with. */
  projectHasAgent: boolean;
}): string {
  const kept = 'Their earlier answers stay on record.';
  if (!facts.isGroupMember) {
    return `They will run it again from the first stop, in their own session. ${kept}`;
  }
  if (!facts.projectHasAgent) {
    return (
      "They ran this with a group. This site has no onboarding agent to set up a session of " +
      `their own, so they will redo it in the group's session. ${kept}`
    );
  }
  return (
    'They ran this with a group. They will leave the group and redo it on their own, ' +
    'from the first stop, in a new session of their own (it takes a moment to set up). ' +
    'To redo it with a group instead, regroup them from their cohort pairing afterwards, ' +
    `if it is still active. ${kept}`
  );
}
